import * as React from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * App-wide replacement for the plain browser `<input type="date">`.
 * Renders a styled trigger button + popover Calendar with dropdown
 * month/year selectors (captionLayout="dropdown") so jumping to any
 * date/month/year is a couple of clicks instead of paging month-by-month.
 * value/onChange use plain "yyyy-MM-dd" strings to match how every
 * existing form field in the app already stores dates.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  required,
  fromYear = 1930,
  toYear = new Date().getFullYear() + 5,
  testId,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = value && isValid(parseISO(value)) ? parseISO(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          data-required={required || undefined}
          className={cn(
            "h-10 w-full justify-start border-slate-200 text-left font-normal hover:bg-slate-50",
            !selected && "text-slate-400",
            className
          )}
        >
          <CalendarDays className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
          {selected ? format(selected, "dd MMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
